import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Stack,
  Text,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner
} from '@chakra-ui/react';
import { Plus, Trash, CaretDown } from '@phosphor-icons/react';
import { Field } from '@/components/ui/field';
import { ApplicationQuestion } from '@/types/ApplicationQuestion';
import {
  ApplicationQuestionType,
  getApplicationQuestionTypeLabel
} from '@/types/enums/ApplicationQuestionType';

interface QuestionEditorProps {
  question: ApplicationQuestion;
  index: number;
  isDisabled: boolean;
  error?: string;
  onQuestionChange: (questionId: string, value: string) => void;
  onQuestionTypeChange: (questionId: string, type: ApplicationQuestionType) => void;
  onRemoveQuestion: (questionId: string) => void;
  onOptionChange: (questionId: string, optionIndex: number, value: string) => void;
  onAddOption: (questionId: string) => void;
  onRemoveOption: (questionId: string, optionIndex: number) => void;
}

export default function QuestionEditor({
  question,
  index,
  isDisabled,
  error,
  onQuestionChange,
  onQuestionTypeChange,
  onRemoveQuestion,
  onOptionChange,
  onAddOption,
  onRemoveOption
}: QuestionEditorProps) {
  return (
    <Box p="20px" borderRadius="12px" border="1px solid" borderColor="neutrals.200" bg="neutrals.0">
      <Flex justifyContent="space-between" alignItems="flex-start" mb="16px">
        <Text fontSize="16px" fontWeight="semibold" color="neutrals.800">
          Question {index + 1}
        </Text>
        <IconButton
          variant="ghost"
          size="sm"
          color="error.500"
          borderRadius="full"
          _hover={{ bg: 'error.50' }}
          onClick={() => onRemoveQuestion(question.question_id)}
          disabled={isDisabled}
          aria-label="Remove question"
        >
          <Trash size={18} />
        </IconButton>
      </Flex>

      <Stack gap="16px">
        <Field
          label={
            <Text fontSize="16px" color="neutrals.700">
              Question Text
            </Text>
          }
          invalid={!!error}
          errorText={error}
        >
          <Input
            placeholder="Enter your question here"
            value={question.question}
            onChange={(e) => onQuestionChange(question.question_id, e.target.value)}
            disabled={isDisabled}
            size="lg"
            borderRadius="8px"
            fontSize="16px"
          />
        </Field>

        <Flex gap="16px" align="flex-end">
          <Field
            label={
              <Text fontSize="16px" color="neutrals.700">
                Question Type
              </Text>
            }
            flex="1"
          >
            <MenuRoot>
              <MenuTrigger asChild>
                <Button
                  variant="outline"
                  justifyContent="space-between"
                  w="100%"
                  size="lg"
                  border="1px solid"
                  borderColor="neutrals.300"
                  borderRadius="8px"
                  disabled={isDisabled}
                >
                  <Text fontSize="16px" color="neutrals.700">
                    {getApplicationQuestionTypeLabel(question.question_type)}
                  </Text>
                  <CaretDown size={16} />
                </Button>
              </MenuTrigger>
              <MenuPositioner>
                <MenuContent
                  border="1px solid"
                  borderColor="neutrals.200"
                  borderRadius="8px"
                  p="8px"
                  minW="200px"
                >
                  <MenuItem
                    value="text"
                    cursor="pointer"
                    onClick={() => onQuestionTypeChange(question.question_id, 'text')}
                    bg={question.question_type === 'text' ? 'primary.50' : 'transparent'}
                    borderRadius="6px"
                    _hover={{ bg: 'primary.50' }}
                  >
                    <Text fontSize="16px">Short Answer</Text>
                  </MenuItem>
                  <MenuItem
                    value="yes_no"
                    cursor="pointer"
                    onClick={() => onQuestionTypeChange(question.question_id, 'yes_no')}
                    bg={question.question_type === 'yes_no' ? 'primary.50' : 'transparent'}
                    borderRadius="6px"
                    _hover={{ bg: 'primary.50' }}
                  >
                    <Text fontSize="16px">Yes/No</Text>
                  </MenuItem>
                  <MenuItem
                    value="multiple_choice"
                    cursor="pointer"
                    onClick={() => onQuestionTypeChange(question.question_id, 'multiple_choice')}
                    bg={question.question_type === 'multiple_choice' ? 'primary.50' : 'transparent'}
                    borderRadius="6px"
                    _hover={{ bg: 'primary.50' }}
                  >
                    <Text fontSize="16px">Multiple Choice</Text>
                  </MenuItem>
                </MenuContent>
              </MenuPositioner>
            </MenuRoot>
          </Field>
        </Flex>

        {question.question_type === 'multiple_choice' && (
          <Box>
            <Text fontSize="16px" fontWeight="medium" color="neutrals.800" mb="8px">
              Options
            </Text>
            <Stack gap="8px">
              {(question.options || []).map((option, optIndex) => (
                <Flex key={optIndex} gap="8px" align="center">
                  <Input
                    placeholder={`Option ${optIndex + 1}`}
                    value={option}
                    onChange={(e) => onOptionChange(question.question_id, optIndex, e.target.value)}
                    disabled={isDisabled}
                    size="md"
                    borderRadius="8px"
                    fontSize="14px"
                  />
                  <IconButton
                    variant="ghost"
                    size="sm"
                    color="error.500"
                    borderRadius="full"
                    _hover={{ bg: 'error.50' }}
                    onClick={() => onRemoveOption(question.question_id, optIndex)}
                    disabled={isDisabled || (question.options?.length || 0) <= 1}
                    aria-label="Remove option"
                  >
                    <Trash size={16} />
                  </IconButton>
                </Flex>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddOption(question.question_id)}
                disabled={isDisabled}
                borderRadius="8px"
                color="primary.900"
                fontSize="14px"
                _hover={{ bg: 'primary.50' }}
                w="fit-content"
              >
                <Plus size={16} weight="bold" />
                Add Option
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
